import { defaultProps } from "helpers/common";
import { PropsWithChildren, ReactNode } from "react";
import AppNavSteps, { AppNavStepsProps } from "components/v2/molecules/app-nav-steps/AppNavSteps";
import { Container, ContainerProps, Stack, StackProps, Typography, TypographyProps } from "@mui/material";

export interface V2ContainerProps extends PropsWithChildren {
    containerProps?: ContainerProps;
    stackProps?: StackProps;
    textStackProps?: StackProps;
    appNavStepsProps?: AppNavStepsProps;
    topHeadingProps?: TypographyProps;
    heading?: ReactNode;
    subHeading?: ReactNode;
    headingProps?: TypographyProps;
    subHeadingProps?: TypographyProps;
    redirectTo?: ReactNode;
    subTitle?: ReactNode;
    fullWidthHeader?: boolean;
    disableFullHeight?: boolean;
}

function V2Container(props: V2ContainerProps) {
    const containerProps = defaultProps(props.containerProps || {}, {
        maxWidth: "xs",
        disableGutters: true,
        sx: {
            ...(!props.disableFullHeight && { height: "100%" }),
            ...(!props.fullWidthHeader && {
                py: {
                    xs: 2,
                    sm: 1,
                },
                px: {
                    xs: 2,
                    sm: 0,
                },
            }),
        },
    });

    const stackProps = defaultProps(props.stackProps || {}, {
        sx: {
            gap: 3,
            height: "100%",
            mt: {
                xs: 0,
                sm: 3,
            },
        },
    });

    const headerStackProps = defaultProps(props.textStackProps || {}, {
        sx: {
            gap: 1,
            ...(props.fullWidthHeader && {
                pt: { xs: 2, sm: 0 },
                pb: { xs: 2, sm: 0 },
                px: { xs: 2, sm: 0 },
                backgroundColor: {
                    xs: "white",
                    sm: "background.default",
                },
                borderBottom: {
                    xs: "2px solid #EDF0F3",
                    sm: "none",
                },
            }),
        },
    });

    const headingProps = defaultProps(props.headingProps || {}, {
        variant: "h1",
        sx: {
            textAlign: {
                xs: "left",
                sm: "center",
            },
        },
        children: props.heading,
    });

    const subHeadingProps = defaultProps(props.subHeadingProps || {}, {
        sx: {
            fontSize: 15,
            fontWeight: 500,
            textAlign: {
                xs: "left",
                sm: "center",
            },
            color: "text.disabled",
        },
        children: props.subHeading,
    });

    const topHeadingProps = defaultProps(props.topHeadingProps || {}, {
        sx: {
            fontSize: 15,
            color: "text.disabled",
            fontWeight: 500,
            textAlign: {
                xs: "left",
                sm: "center",
            },
        },
        children: props.subHeading,
    });

    const navStepsProps = defaultProps(props.stackProps || {}, {
        sx: {
            ...(props.fullWidthHeader && {
                pt: { xs: 2, sm: 0 },
                pb: { xs: 2, sm: 0 },
                px: { xs: 2, sm: 0 },
                backgroundColor: {
                    xs: "white",
                    sm: "background.default",
                },
                borderBottom: {
                    xs: "2px solid #EDF0F3",
                    sm: "none",
                },
            }),
        },
    });

    const hasHeading = !!props.heading;
    const hasSubHeading = !!props.subHeading;
    const topHeading = !!props.topHeadingProps;

    return (
        <Container {...containerProps}>
            <Stack {...stackProps}>
                {props.redirectTo}
                {props.appNavStepsProps && (
                    <Stack {...navStepsProps}>
                        <AppNavSteps {...props.appNavStepsProps} />
                    </Stack>
                )}
                {(hasHeading || hasSubHeading) && (
                    <Stack {...headerStackProps}>
                        {topHeading && <Typography {...topHeadingProps} />}
                        {hasHeading && <Typography {...headingProps} />}
                        {hasSubHeading && <Typography {...subHeadingProps} />}
                        {props.subTitle}
                    </Stack>
                )}
                {props.children}
            </Stack>
        </Container>
    );
}

export default V2Container;
